(ns ui.view.entity-graph.utils
  (:require [reagent-flow.core :as flow]
            [re-frame.core :as rf]
            [common.schema.entities.graph-reference :as graph-reference]
            [ui.state.session.sub :as session-sub]
            [ui.state.session.model :as session-model]
            [goog.string :as gstring]
            [goog.string.format]
            [clojure.set :as set]
            [ui.view.entity-graph.model :as m]))

(defn get-entity
  [id]
  @(rf/subscribe [::session-sub/entity id]))

(def default-order
  (->> [:graph-entity/entity
        :graph-entity/label
        :graph-entity/business-id
        :graph-entity/jurisdiction
        :graph-entity/domicile
        :graph-entity/legal-form
        :graph-entity​/giin
        :graph-entity​/tin
        :graph-entity​/duns-number
        :graph-entity/first-name
        :graph-entity/last-name
        :graph-entity/date-of-birth
        :graph-entity/identification-number
        :graph-entity/country-of-residence]
       (map-indexed (fn [i a]
                      [a i]))
       (into {})))

(defn attr-order
  [a]
  (get default-order a 10000))

(get default-order :graph-entity/entity)

(defn find-metadata
  [{:keys [graph-reference/metadata] :as _graph-reference} attribute]
  (let [item (->> metadata
                  (map get-entity)
                  (filter (fn [m]
                            (contains? m attribute)))
                  first)]
    item))

(defn metadata-value
  [{:keys [graph-reference/metadata] :as _graph-reference} attribute]
  (let [item (->> metadata
                  (map get-entity)
                  (filter (fn [m]
                            (contains? m attribute)))
                  first)]
    (get item attribute)))

(defn ownership-percentage
  [graph-reference]
  (let [attribute :graph-meta/ownership-percentage]
    (-> (find-metadata graph-reference attribute)
        (get attribute))))

(defn format-percentage
  [percentage]
  (when (number? percentage)
    (gstring/format "%.2f%" (* 100 percentage))))

(def node-type-by-entity-type
  {:entity.type/company :company-node
   :entity.type/person :person-node
   :entity.type/fof-asset :fund-node
   ;; note: might be later necessary to distinguish between
   ;; fof-asset and (our-)fund:
   :entity.type/fund :fund-node})

;; hard-coded for now - could also be dynamic; received
;;  from react-flow's "dimensions" events
(defn wh
  [_id]
  ;;@(rf/subscribe [m/dimensions (assoc )])
  {:width m/WIDTH, :height m/HEIGHT})

;; turns map-profile into format reagent-flow is expecting
(defn flow-node
  [{:keys [selected? reconnecting? _no-reconnecting? position ctx]}
   {:graph-entity/keys [entity entity-type label]
    id :db/id :as _map-profile}]
  (let [node-type (get node-type-by-entity-type entity-type)
        node-id (str id)]
    (cond-> {:id node-id
             :type node-type
             :connectable (boolean reconnecting?)
             :class-name node-type
             :position (or position {:x 0 :y 0})
             :data {:label label
                    :map-profile-id id
                    :reconnecting reconnecting?
                    :main-entity-id entity
                    :ctx ctx}
             ;;:width WIDTH
             ;;:height HEIGHT
             :measured (wh id)}
      selected? (assoc :selected true))))

(def source-handle-mapping
  {:bottom "bottom-source-handle"
   :right "right-source-handle"
   :left "left-source-handle"
   :top "top-source-handle"})

(def target-handle-mapping
  {:left "left-target-handle"
   :top "top-target-handle"
   :right "right-target-handle"
   :bottom "bottom-target-handle"})

(def handle-string-mapping
  (merge
   (set/map-invert source-handle-mapping)
   (set/map-invert target-handle-mapping)))

(defn resolve-source-handle
  [{:keys [source-handle connector-type]}]
  (let [source-handle (or source-handle
                          (get m/default-source-handle-by-type
                               connector-type))]
    (cond
      (contains? source-handle-mapping source-handle)
      (get source-handle-mapping source-handle)

      :else
      "bottom-source-handle")))

(defn resolve-target-handle
  [{:keys [target-handle connector-type]}]
  (let [target-handle (or target-handle
                          (get m/default-target-handle-by-type
                               connector-type))]
    (cond
      (contains? target-handle-mapping target-handle)
      (get target-handle-mapping target-handle)

      :else
      "top-target-handle")))

(defn flow-edge
  [{:keys [selected? one-selected-visible? source-handle target-handle ctx]}
   {:keys [db/id]
    :graph-reference/keys [source target type]}]
  (let [edge-id (str id)]
    (cond-> {:id edge-id
             :source (str source)
             :target (str target)
             :data {:connector-id id
                    :ctx ctx}
             :reconnectable (boolean one-selected-visible?)
             :source-handle (resolve-source-handle {:connector-type type
                                                    :source-handle source-handle})
             :target-handle (resolve-target-handle {:connector-type type
                                                    :target-handle target-handle})
             :marker-end
             {:type (.-ArrowClosed flow/FlowMarkerType)
              :width 20 :height 20
              ;; TODO colors based on theme:
              :color (if selected?
                       "black"
                       "gray")}}
      selected? (assoc :selected true
                       ;; needed for proper handling of draggable
                       ;;  source/target handle location updates
                       :z-index 1)
      one-selected-visible? (assoc :z-index 2))))

(defn find-main-map-profile
  [{:keys [entities main-entity-id]}]
  (->> entities
       (filterv (fn [{:keys [graph-entity/entity]}]
                  (= entity main-entity-id)))
       (sort-by :common/updated-on)
       last))

(defn connection-tuples
  [{:keys [connectors]}]
  (->> connectors
       (mapv (fn [c]
               [(:graph-reference/source c)
                (:graph-reference/target c)
                (:graph-reference/type c)
                (:db/id c)]))))

(defn connection-tuples-by-id
  [{:keys [connection-tuples]}]
  (->> connection-tuples
       (reduce (fn [m [source target :as tuple]]
                 (let [source-tuples (conj (get m source []) tuple)
                       target-tuples (conj (get m target []) tuple)]
                   (assoc! m source source-tuples target target-tuples)))
               (transient {}))
       persistent!))

(defn hidden-connection?
  [hide-rules [source target connector-type connector-id :as _connection-tuple]]
  (let [{:keys [map-profiles connectors system-config]} hide-rules]
    (if system-config
      (contains? (get-in m/system-level-configs
                         [system-config :hide-connector-types])
                 connector-type)
      (or (contains? connectors connector-id)
          (contains? map-profiles source)
          (contains? map-profiles target)))))

(defn other-node
  [node-id [source target :as _connection-tuple]]
  (cond (= source node-id) target
        (= target node-id) source
        :else nil))

(defn mark-as-visible
  "Traverse the graph starting from first element of `to-visit`,
   mark nodes and edges as visible if not hidden based on `hide-rules`"
  [{:keys [visited to-visit visible-nodes visible-edges
           connection-tuples-by-node-id hide-rules]
    :or {visited #{}
         visible-edges #{}
         to-visit []} :as _ctx}]
  (if (empty? to-visit)
    {:nodes visible-nodes
     :edges visible-edges}
    (let [node-id (first to-visit)
          ;; pop the node-id out of to-visit:
          to-visit-start (subvec to-visit 1)

          ;; TODO conj only if _node_ is not hidden - TODO node-specific hide-rules
          new-visible-nodes (conj visible-nodes node-id)

          new-visited (conj visited node-id)

          ;; nodes reachable from node-id
          visible-node-tuples (->> (get connection-tuples-by-node-id node-id)
                                   (into
                                    #{}
                                    (remove (partial hidden-connection? hide-rules))))
          new-visible-edges (->> visible-node-tuples
                                 (into
                                  visible-edges
                                  (map (fn [[_source _target _type connector-id]]
                                         connector-id))))
          connected-nodes (->> visible-node-tuples
                               (into
                                []
                                (comp
                                 ;; get the connected nodes
                                 (map (partial other-node node-id))
                                 ;; if not already visited
                                 (remove #(contains? new-visited %)))))
          new-to-visit (into to-visit-start connected-nodes)]
      (recur {:connection-tuples-by-node-id connection-tuples-by-node-id
              :hide-rules hide-rules
              :visible-nodes new-visible-nodes
              :visible-edges new-visible-edges
              :visited new-visited
              :to-visit new-to-visit}))))

(defn get-graph-entities
  [entities-by-type]
  (let [companies (get entities-by-type :entity.type/graph-company)
        persons (get entities-by-type :entity.type/graph-person)
        fof-assets (get entities-by-type :entity.type/graph-fof-asset)
        funds (get entities-by-type :entity.type/graph-fund)]
    (-> (vec companies)
        (into persons)
        (into fof-assets)
        (into funds))))

(defn visible-nodes-edges
  [{:keys [db]} {:keys [main-entity-id] :as ctx}]
  (let [entities-by-id (get-in db session-model/entities-path)
        entities (vals entities-by-id)
        entities-by-type (group-by :entity/type entities)
        map-profiles (get-graph-entities entities-by-type)
        main-map-profile (find-main-map-profile
                          {:entities map-profiles
                           :main-entity-id main-entity-id})
        main-map-profile-id (:db/id main-map-profile)
        graph-references (get entities-by-type :entity.type/graph-reference)
        connectors (->> graph-references
                        (filterv (fn [{:keys [graph-reference/type]}]
                                   (contains? graph-reference/connector-types type))))
        ;; non-connectors: board-member, auditors, etc connections
        ;;  must be shown for visible map-profiles even when other party
        ;;  (source/target map-profile) might be hidden
        connection-tuples (connection-tuples {:connectors connectors})
        connection-tuples-by-node-id (connection-tuples-by-id
                                      {:connection-tuples connection-tuples})
        system-config (get-in db (m/selected-system-config-full-path ctx))
        hide-rules (get-in db (m/hide-rules-full-path ctx))
        hide-rules (if system-config
                     {:system-config system-config}
                     hide-rules)
        ;; traverse graph to find visible nodes and edges
        {:keys [nodes edges]} (mark-as-visible
                               {:visited #{}
                                :visible-nodes #{main-map-profile-id}
                                :visible-edges #{}
                                :hide-rules hide-rules
                                :connection-tuples-by-node-id connection-tuples-by-node-id
                                :to-visit [main-map-profile-id]})
        visible-map-profiles (->> map-profiles
                                  (filterv (fn [{:keys [db/id]}]
                                             (contains? nodes id))))
        visible-connectors (->> connectors
                                (filterv (fn [{:keys [db/id]}]
                                           (contains? edges id))))]
    {:nodes nodes
     :edges edges
     :entities-by-id entities-by-id
     :visible-map-profiles visible-map-profiles
     :visible-connectors visible-connectors}))

(defn get-visible-data
  [{:keys [db]} {:keys [main-entity-id] :as ctx}]
  (let [entities-by-id (get-in db session-model/entities-path)
        entities (vals entities-by-id)
        entities-by-type (group-by :entity/type entities)
        map-profiles (get-graph-entities entities-by-type)
        main-map-profile (find-main-map-profile
                          {:entities map-profiles
                           :main-entity-id main-entity-id})
        main-map-profile-id (:db/id main-map-profile)
        graph-references (get entities-by-type :entity.type/graph-reference)
        connectors (->> graph-references
                        (filterv (fn [{:keys [graph-reference/type]}]
                                   (contains? graph-reference/connector-types type))))
        other-graph-refs (->> graph-references
                              (filterv (fn [{:keys [graph-reference/type]}]
                                         (not (contains?
                                               graph-reference/connector-types
                                               type)))))
        ;; non-connectors: board-member, auditors, etc connections
        ;;  must be shown for visible map-profiles even when other party
        ;;  (source/target map-profile) might be hidden
        other-ref-tuples-by-node-id (connection-tuples-by-id
                                     {:connection-tuples
                                      (connection-tuples
                                       {:connectors other-graph-refs})})
        connection-tuples (connection-tuples {:connectors connectors})
        connection-tuples-by-node-id (connection-tuples-by-id
                                      {:connection-tuples connection-tuples})
        hide-rules (get-in db (m/hide-rules-full-path ctx))
        system-config (get-in db (m/selected-system-config-full-path ctx))
        hide-rules (if system-config
                     {:system-config system-config}
                     hide-rules)
        ;; traverse graph to find visible nodes and edges
        {:keys [nodes edges]} (mark-as-visible
                               {:visited #{}
                                :visible-nodes #{main-map-profile-id}
                                :visible-edges #{}
                                :hide-rules hide-rules
                                :connection-tuples-by-node-id connection-tuples-by-node-id
                                :to-visit [main-map-profile-id]})
        visible-connectors (->> connectors
                                (filterv (fn [{:keys [db/id]}]
                                           (contains? edges id))))
        visible-map-profiles (->> map-profiles
                                  (filterv (fn [{:keys [db/id]}]
                                             (contains? nodes id))))
        ;; other-connections for visible-map-profiles
        other-ref-tuples (->> (select-keys other-ref-tuples-by-node-id nodes)
                              (mapcat second))
        other-ref-ids (->> other-ref-tuples
                           (into
                            #{}
                            (map (fn [[_source _target _type ref-id]]
                                   ref-id))))
        ;; hidden map-profiles that need to be exported due to having a
        ;; non-connector graph-reference to a visible map-profile
        other-ref-map-profile-ids (->> other-ref-tuples
                                       (into
                                        #{}
                                        (mapcat (fn [[source target _type _ref-id]]
                                                  (cond
                                                    ;; shouldn't happen:
                                                    (and (not (contains? nodes source))
                                                         (not (contains? nodes target)))
                                                    [source target]

                                                    (not (contains? nodes source))
                                                    [source]

                                                    (not (contains? nodes target))
                                                    [target]

                                                    :else
                                                    nil)))))
        hidden-needed-map-profiles (->> map-profiles
                                        (filterv (fn [{:keys [db/id]}]
                                                   (contains?
                                                    other-ref-map-profile-ids
                                                    id))))
        other-connections (->> other-graph-refs
                               (filterv (fn [{:keys [db/id]}]
                                          (contains? other-ref-ids id))))]
    {:nodes nodes
     :edges edges
     :entities-by-id entities-by-id
     :visible-map-profiles visible-map-profiles
     :hidden-needed-map-profiles hidden-needed-map-profiles
     :visible-connectors visible-connectors
     :other-graph-references other-connections}))

;; used for figuring out connectors to add to hide-rules when moving
;; from system-level view-config to normal view-config
;; (i.e. explicitly hidden set of connectors)
(defn get-reachable-connectors
  [{:keys [db]} {:keys [main-entity-id] :as _ctx}]
  (let [entities-by-id (get-in db session-model/entities-path)
        entities (vals entities-by-id)
        entities-by-type (group-by :entity/type entities)
        map-profiles (get-graph-entities entities-by-type)
        main-map-profile (find-main-map-profile
                          {:entities map-profiles
                           :main-entity-id main-entity-id})
        main-map-profile-id (:db/id main-map-profile)
        graph-references (get entities-by-type :entity.type/graph-reference)
        connectors (->> graph-references
                        (filterv (fn [{:keys [graph-reference/type]}]
                                   (contains? graph-reference/connector-types type))))
        connection-tuples (connection-tuples {:connectors connectors})
        connection-tuples-by-node-id (connection-tuples-by-id
                                      {:connection-tuples connection-tuples})
        ;; no hide-rules - find everything reachable from main-map-profile-id
        hide-rules #{}
        ;; traverse graph to find visible nodes and edges
        {:keys [edges]} (mark-as-visible
                         {:visited #{}
                          :visible-nodes #{main-map-profile-id}
                          :visible-edges #{}
                          :hide-rules hide-rules
                          :connection-tuples-by-node-id connection-tuples-by-node-id
                          :to-visit [main-map-profile-id]})
        reachable-connectors (->> connectors
                                  (filterv (fn [{:keys [db/id]}]
                                             (contains? edges id))))]
    reachable-connectors))

;; later might need to check whether graph-reference-type has editable fields in-use etc.
(defn editable-graph-reference?
  [_ctx {:keys [graph-reference/type]}]
  (contains? graph-reference/graph-references-metadata type))
